import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/**
 * Generate or retrieve a session ID for anonymous visitor tracking.
 * Stored in sessionStorage so it persists per tab session but not across sessions.
 */
function getSessionId(): string {
  const key = 'kv_session_id';
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
}

/**
 * Lightweight page tracking hook. Fires on every route change.
 * - Non-blocking (fire-and-forget)
 * - Catches all errors silently
 * - Does not affect rendering performance
 */
export function usePageTracking() {
  const location = useLocation();
  const startTimeRef = useRef<number>(Date.now());
  const lastPathRef = useRef<string>('');

  useEffect(() => {
    const currentPath = location.pathname;

    // Send duration for the previous page (if any)
    if (lastPathRef.current && lastPathRef.current !== currentPath) {
      const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
      if (duration > 0 && duration < 3600) {
        // Fire-and-forget duration update
        try {
          navigator.sendBeacon(
            `${API_BASE}/analytics/pageview`,
            JSON.stringify({
              path: lastPathRef.current,
              sessionId: getSessionId(),
              duration,
            })
          );
        } catch {
          // Silent fail
        }
      }
    }

    // Track the new page view
    startTimeRef.current = Date.now();
    lastPathRef.current = currentPath;

    const trackView = async () => {
      try {
        const payload = {
          path: currentPath,
          title: document.title,
          referrer: document.referrer || null,
          sessionId: getSessionId(),
        };

        // Use fetch with keepalive for non-blocking request
        fetch(`${API_BASE}/analytics/pageview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {
          // Silent fail - analytics should never break the user experience
        });
      } catch {
        // Silent fail
      }
    };

    trackView();
  }, [location.pathname]);

  // Track duration on page unload
  useEffect(() => {
    const handleUnload = () => {
      const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
      if (duration > 0 && duration < 3600 && lastPathRef.current) {
        try {
          navigator.sendBeacon(
            `${API_BASE}/analytics/pageview`,
            JSON.stringify({
              path: lastPathRef.current,
              sessionId: getSessionId(),
              duration,
            })
          );
        } catch {
          // Silent fail
        }
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);
}
